// @ts-check
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';

/**
 * @typedef GlobalLayoutOptions
 * @property {import('~/templates/CSTypes').CSFile} favicon
 * @property {HeaderOptions} header
 * @property {import('~/components/Core/Footer/Footer').FooterOptions} footer
 *
 * @typedef HeaderOptions
 * @property {HeaderUtilLinks[]} util_links
 * @property {HeaderLogoOptions} logo
 * @property {import('~/templates/CSTypes').CSLink} open_account_button
 * @property {import('~/templates/CSTypes').CSLink} login_button
 * @property {HeaderNavigation} navigation
 *
 * @typedef HeaderUtilLinks
 * @property {import('~/templates/CSTypes').CSLink} link
 * @property {boolean} open_in_new_tab
 * @property {string[]} visible_on
 * @property {import('~/templates/CSTypes').CSFile} [icon]
 *
 * @typedef HeaderLogoOptions
 * @property {import('~/templates/CSTypes').CSLink} link
 * @property {import('~/templates/CSTypes').CSFile} image
 *
 * @typedef HeaderNavigation
 * @property {HeaderNavigationFirst[]} first_level
 *
 * @typedef HeaderNavigationFirst
 * @property {import('~/templates/CSTypes').CSLink} link
 * @property {boolean} open_in_new_tab
 * @property {string[]} visible_on
 * @property {import('~/templates/CSTypes').CSFile} [icon]
 * @property {HeaderNavigationSecond[]} second_level
 *
 * @typedef HeaderNavigationSecond
 * @property {import('~/templates/CSTypes').CSLink} link
 * @property {boolean} open_in_new_tab
 * @property {HeaderNavigationThird[]} third_level
 *
 * @typedef HeaderNavigationThird
 * @property {import('~/templates/CSTypes').CSLink} link
 * @property {boolean} open_in_new_tab
 */
/** @returns {GlobalLayoutOptions | null} */
export const useGlobalLayout = () => {
	/** @type {{ ozkGlobalLayout: GlobalLayoutOptions }} */
	const staticData = useStaticQuery(graphql`
		query GlobalLayout {
			ozkGlobalLayout {
				title
				uid
				_version
				favicon {
					...AssetFragment
				}
				header {
					util_links {
						link {
							title
							href
						}
						open_in_new_tab
						visible_on
						icon {
							...AssetFragment
						}
					}
					logo {
						image {
							...AssetFragment
						}
						link {
							title
							href
						}
					}
					open_account_button {
						title
						href
					}
					login_button {
						title
						href
					}

					navigation {
						first_level {
							link {
								title
								href
							}
							open_in_new_tab
							visible_on
							icon {
								...AssetFragment
							}
							second_level {
								link {
									title
									href
								}
								open_in_new_tab
								third_level {
									link {
										title
										href
									}
									open_in_new_tab
								}
							}
						}
					}
				}

				footer {
					top_logo {
						link {
							title
							href
						}
						logo {
							...AssetFragment
						}
					}
					pre_copy {
						copy {
							json
						}
					}
					center_columns {
						header
						link {
							title
							href
						}
						list {
							json
						}
					}
					post_copy {
						copy {
							json
						}
					}
					socials_icons {
						link {
							title
							href
						}
						icon {
							...AssetFragment
						}
					}
					bank_logos {
						alt_text
						logo {
							...AssetFragment
						}
					}
				}
			}
		}
	`);

	return staticData?.ozkGlobalLayout ?? null;
};

/**
 * @function
 * @param {HeaderNavigationFirst[]} links
 * @returns {import('~/components/Core/SiteLinks').LinkItem[]}
 */
export function ConvertNavigationToLinkItem(links) {
	return (
		links?.map((first) => {
			const firstChildren = first.second_level?.map((second) => {
				const secondChildren = second.third_level.map((third) => {
					return {
						name: third.link.title,
						openInNewTab: third.open_in_new_tab,
						link: third.link.href,
					};
				});

				return {
					name: second.link.title,
					link: second.link.href,
					openInNewTab: second.open_in_new_tab,
					children: secondChildren,
				};
			});

			return {
				name: first.link.title,
				link: first.link.href,
				desktopOnly: !first.visible_on?.includes('mobile') && first.visible_on?.includes('desktop'),
				mobileOnly: first.visible_on?.includes('mobile') && !first.visible_on?.includes('desktop'),
				leftComponent: !first?.icon ? undefined : (
					<img
						className="primary-nav-icon"
						src={first.icon?.url}
						alt={first.link?.title}
						aria-label={first.link?.title}
					/>
				),
				openInNewTab: first.open_in_new_tab,
				children: firstChildren,
			};
		}) ?? []
	);
}
